/**
 * The default line items a contract starts from, by region and deal type.
 *
 * ONE SOURCE OF TRUTH. These item ids are QuickBooks item ids, and they have
 * already been renamed once under us -- see `transport-line.ts`, where both
 * contract templates broke when the QB items became "ON | ..." / "QC | ..." on
 * 2026-08-27. A second copy is how that happens. This package exists so the
 * admin contract form and the NestJS auto-creator read the same constant
 * rather than two that agree until they don't.
 *
 * Consumers: admin's contract form and the three "Add contract" buttons, the
 * admin Pricing page, and `ContractAutoCreateService` in NestJS.
 */
export type DefaultContractLine = {
  item: string;
  quantity?: number;
};

const isProdMod = true;

const productionAtNeed: DefaultContractLine[] = [{ item: '1010000041' }];
const productionPreNeed: DefaultContractLine[] = [{ item: '20' }];

const nonProductionAtNeed: DefaultContractLine[] = [
  { item: '30' },
  { item: '32' },
  { item: '33' },
];

const nonProductionPreNeed: DefaultContractLine[] = [
  { item: '39' },
  { item: '32', quantity: 2 },
  { item: '33' },
];

const atNeedDefaults = isProdMod ? productionAtNeed : nonProductionAtNeed;
const preNeedDefaults = isProdMod ? productionPreNeed : nonProductionPreNeed;

export const AT_NEED_DEFAULT_LINES = atNeedDefaults.map((line) => ({ ...line }));
export const PRE_NEED_DEFAULT_LINES = preNeedDefaults.map((line) => ({ ...line }));

// Ontario-specific default lines
export const ONTARIO_AT_NEED_DEFAULT_LINES: DefaultContractLine[] = [
  { item: '24', quantity: 1 },
  { item: '25', quantity: 1 },
  { item: '26', quantity: 1 },
  { item: '27', quantity: 1 },
  { item: '28', quantity: 1 },
];

// Ontario preneed is priced the same as Ontario at-need, so it uses the same
// items. (Previously ONtario preneed fell through to the Quebec preneed item
// '20', which has QC tax/accounts.)
export const ONTARIO_PRE_NEED_DEFAULT_LINES: DefaultContractLine[] = [
  { item: '24', quantity: 1 },
  { item: '25', quantity: 1 },
  { item: '26', quantity: 1 },
  { item: '27', quantity: 1 },
  { item: '28', quantity: 1 },
];

/**
 * The Cleo display urn, per province.
 *
 * Two items rather than one, for the same reason transport has two (see
 * `admin/src/pricing/zone.ts`): a QuickBooks item carries its own
 * `SalesTaxCodeRef`, every line takes its tax code from the item it names, and
 * nothing about the customer's province influences it. One QC-coded urn sold in
 * both provinces is how six Ontario families ended up charged Quebec sales tax
 * between April and September 2026 — the picker filled in code 7 and only
 * whoever noticed changed it.
 *
 * A null here means no urn line is written for that province at all, which is
 * the safe failure and how Ontario stood until 2026-09-15 — a missing line is a
 * conversation, a QC-taxed one is a remittance to the wrong province.
 */
export const URN_ITEMS: Record<'quebec' | 'ontario', string | null> = {
  /** Goods and Services:Cleo Urne QC — GST/QST QC (code 7), $300. */
  quebec: '1010000031',
  /** Goods and Services:Cleo Urn ON — HST ON (code 10), $300. Added 2026-09-15. */
  ontario: '35',
};

/**
 * The urn line a contract should carry, given what the family chose.
 *
 * Empty for a cardboard box (included in the package) and for a family's own
 * urn (nothing to sell). No price here: `priceLines` reads the item's
 * `UnitPrice` from QuickBooks, so repricing there reprices everywhere.
 */
export const urnLinesFor = (
  urnChoice?: string | null,
  region?: string,
): DefaultContractLine[] => {
  if (urnChoice !== 'cleo') return [];

  const item =
    region?.toLowerCase() === 'ontario' ? URN_ITEMS.ontario : URN_ITEMS.quebec;

  return item ? [{ item, quantity: 1 }] : [];
};

export const cloneDefaultLines = (type: 'at' | 'pre', region?: string): DefaultContractLine[] => {
  // Use Ontario-specific items for Ontario contracts (both at-need and preneed).
  if (region?.toLowerCase() === 'ontario') {
    const ontarioSource =
      type === 'pre' ? ONTARIO_PRE_NEED_DEFAULT_LINES : ONTARIO_AT_NEED_DEFAULT_LINES;
    return ontarioSource.map((line) => ({
      ...line,
      quantity: line.quantity ?? 1, // Ensure quantity is always set, default to 1
    }));
  }

  // Use default Quebec lines for all other cases
  const source = type === 'pre' ? PRE_NEED_DEFAULT_LINES : AT_NEED_DEFAULT_LINES;
  return source.map((line) => ({
    ...line,
    quantity: line.quantity ?? 1 // Ensure quantity is always set, default to 1
  }));
};

export const linesMatchDefaults = (
  lines: Array<{ item: string | number; quantity?: number }>,
  defaults: DefaultContractLine[]
): boolean => {
  if (!Array.isArray(lines) || lines.length !== defaults.length) {
    return false;
  }

  return lines.every((line, index) => {
    const target = defaults[index];
    const itemsMatch = String(line.item) === target.item;
    const quantitiesMatch =
      (line.quantity ?? 1) === (target.quantity ?? 1);
    return itemsMatch && quantitiesMatch;
  });
};
